import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Row,
  Col,
  Form,
  Badge,
  Card,
  Dropdown,
  ButtonGroup,
  Container,
  Button,
  Tab,
} from 'react-bootstrap'
import {
  useGetContentMetadataQuery,
  useSampleContentQuery,
  useGetContentImageQuery,
} from '../../../services/contentApi'
import { useAppSelector, useAppDispatch, useAnnotationStats } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { setDatasetSources } from '../../../store/slices/datasetSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { useDatasetStats, type DatasetStats } from '../../../utils/datasetStats'
import {
  Search,
  Database,
  Tag,
  Image,
  Layers,
  Check,
  Grid,
  List,
  Filter,
  ArrowUpDown,
  BarChart3,
  Package,
  Plus,
  Eye,
  FileText,
} from 'lucide-react'
import styled from 'styled-components'
import { PageWithTabs } from '../../common'
import DatasetManageContent from '../DatasetManageContent/DatasetManageContent'
import UploadWizard from '../../wizards/UploadWizard'
import DisplacementChart from '../DisplacementChart/DisplacementChart'

const QuickStatsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
`

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`

const QuickStat = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.8);

  .icon {
    width: 20px;
    height: 20px;
    padding: 4px;
    border-radius: 6px;
    background: ${(props) => props.$color || '#800085'};
    color: white;
  }

  .value {
    font-weight: 600;
    font-size: 1.1rem;
  }

  .label {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
  }
`

const CreateButton = styled.button`
  background: linear-gradient(135deg, #553c9a, #6b46c1);
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
  font-size: 1rem;

  &:hover,
  &:focus {
    background: linear-gradient(135deg, #6b46c1, #7c3aed);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(85, 60, 154, 0.3);
  }
`

const MainContent = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  gap: 1.25rem;
  transition: all 0.2s ease;
  overflow: visible;
`

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem 0;
`

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 300px;
`

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255, 255, 255, 0.5);
    z-index: 2;
  }
`

const SearchInput = styled.input`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  padding: 0.5rem 0.75rem 0.5rem 2.5rem;
  max-width: 300px;

  &:focus {
    background: transparent;
    border-color: #553c9a;
    color: white;
    box-shadow: 0 0 0 0.2rem rgba(85, 60, 154, 0.25);
    outline: none;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`

const FilterBar = styled.div`
  background: transparent;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);

  .filter-section {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .view-controls {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
`

const SearchContainer = styled.div`
  position: relative;
  min-width: 320px;

  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #b0b0b0;
  }

  .form-control {
    padding-left: 2.75rem;
    height: 46px;
    background: transparent;
    border: 2px solid #404040;
    border-radius: 12px;
    color: #ffffff;
    font-size: 1rem;

    &:focus {
      background: #333333;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
      color: #ffffff;
    }

    &::placeholder {
      color: #808080;
    }
  }
`

const FilterChips = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

const FilterChip = styled.button<{ $isActive: boolean }>`
  background: ${(props) => (props.$isActive ? '#667eea' : 'transparent')};
  color: ${(props) => (props.$isActive ? 'white' : '#b0b0b0')};
  border: 2px solid ${(props) => (props.$isActive ? '#667eea' : '#404040')};
  border-radius: 25px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${(props) => (props.$isActive ? '#5a6fd8' : '#333333')};
    border-color: ${(props) => (props.$isActive ? '#5a6fd8' : '#667eea')};
    color: ${(props) => (props.$isActive ? 'white' : '#ffffff')};
  }
`

const ViewToggle = styled(ButtonGroup)`
  .btn {
    background: transparent;
    border: 2px solid #404040;
    color: #b0b0b0;
    padding: 0.5rem 1rem;

    &.active {
      background: #667eea;
      border-color: #667eea;
      color: white;
    }

    &:hover {
      background: #333333;
      border-color: #667eea;
      color: #ffffff;
    }
  }
`

const CardsContainer = styled.div<{ $hasHover: boolean }>`
  transition: all 0.3s ease;

  .dataset-card-wrapper {
    transition: all 0.3s ease;
    ${(props) =>
      props.$hasHover &&
      `
      opacity: 0.4;
      filter: blur(1px);
      transform: scale(0.98);
    `}
  }

  .dataset-card-wrapper:hover,
  .dataset-card-wrapper.is-hovered {
    opacity: 1 !important;
    filter: none !important;
    transform: scale(1) !important;
    z-index: 10;
    position: relative;
  }
`

const DatasetCard = styled(Card)<{ $isSelected: boolean; $viewMode?: 'grid' | 'list' }>`
  background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
  border: 2px solid
    ${(props) => (props.$isSelected ? 'rgba(102, 126, 234, 0.8)' : 'rgba(102, 126, 234, 0.3)')};
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: visible;
  position: relative;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  height: ${(props) => (props.$viewMode === 'list' ? 'auto' : '100%')};
  z-index: 1;

  ${(props) =>
    props.$viewMode === 'list' &&
    `
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1.5rem;
    min-height: 120px;
  `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${(props) =>
      props.$isSelected
        ? 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
        : 'linear-gradient(90deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'};
    opacity: ${(props) => (props.$isSelected ? 1 : 0.5)};
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
    border-color: ${(props) =>
      props.$isSelected ? 'rgba(102, 126, 234, 1)' : 'rgba(102, 126, 234, 0.5)'};
    background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);

    &::before {
      opacity: 1;
    }
  }

  .card-body {
    padding: ${(props) => (props.$viewMode === 'list' ? '1rem 1.5rem' : '1.5rem')};
    background: transparent;
    position: relative;
    z-index: 1;
    ${(props) =>
      props.$viewMode === 'list' &&
      `
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 1.5rem;
      width: 100%;
    `}
  }

  .selected-badge {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.6), 0 0 0 3px rgba(16, 185, 129, 0.2),
      0 0 20px rgba(16, 185, 129, 0.4);
    border: 3px solid rgba(16, 185, 129, 1);
    z-index: 2;
    animation: pulse 2s ease-in-out infinite;

    @keyframes pulse {
      0%,
      100% {
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.6), 0 0 0 3px rgba(16, 185, 129, 0.2),
          0 0 20px rgba(16, 185, 129, 0.4);
      }
      50% {
        box-shadow: 0 4px 16px rgba(16, 185, 129, 0.8), 0 0 0 4px rgba(16, 185, 129, 0.3),
          0 0 30px rgba(16, 185, 129, 0.6);
      }
    }

    svg {
      color: #10b981;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
    }
  }
`

const CardHeader = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${(props) => (props.$viewMode === 'list' ? '0' : '1rem')};
  padding-bottom: ${(props) => (props.$viewMode === 'list' ? '0' : '1rem')};
  border-bottom: ${(props) =>
    props.$viewMode === 'list' ? 'none' : '1px solid rgba(255, 255, 255, 0.15)'};
  ${(props) =>
    props.$viewMode === 'list' &&
    `
    flex: 1;
    min-width: 0;
  `}
`

const CardTitle = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  flex: 1;

  .dataset-name {
    font-size: ${(props) => (props.$viewMode === 'list' ? '1.1rem' : '1.5rem')};
    font-weight: 700;
    background: linear-gradient(135deg, #ffffff 0%, rgba(102, 126, 234, 0.9) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 0.5rem;
    line-height: 1.3;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    ${(props) =>
      props.$viewMode === 'list' &&
      `
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
  }

  .dataset-source {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 500;
    ${(props) =>
      props.$viewMode === 'list' &&
      `
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
  }
`

const ThumbnailStack = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  position: relative;
  width: ${(props) => (props.$viewMode === 'list' ? '220px' : '100%')};
  height: ${(props) => (props.$viewMode === 'list' ? '100px' : '100px')};
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
  ${(props) => props.$viewMode === 'list' && 'margin-left: 1.5rem;'}
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: ${(props) => (props.$viewMode === 'list' ? 'visible' : 'hidden')};

  .thumbnail-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: ${(props) => (props.$viewMode === 'list' ? 'flex-start' : 'center')};
    ${(props) => props.$viewMode === 'list' && 'overflow: visible;'}
  }

  .thumbnail {
    position: absolute;
    width: 70px;
    height: 70px;
    border-radius: 8px;
    border: 2px solid rgba(102, 126, 234, 0.3);
    overflow: hidden;
    background: rgba(0, 0, 0, 0.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    ${(props) =>
      props.$viewMode === 'list'
        ? `
      &:nth-child(1) {
        left: 0;
        z-index: 5;
        transform: rotate(-8deg) translateY(0);
      }

      &:nth-child(2) {
        left: 30px;
        z-index: 4;
        transform: rotate(-4deg) translateY(0);
      }

      &:nth-child(3) {
        left: 60px;
        z-index: 3;
        transform: rotate(0deg) translateY(0);
      }

      &:nth-child(4) {
        left: 90px;
        z-index: 2;
        transform: rotate(4deg) translateY(0);
      }

      &:nth-child(5) {
        left: 120px;
        z-index: 1;
        transform: rotate(8deg) translateY(0);
        opacity: 0.8;
      }
    `
        : `
      &:nth-child(1) {
        left: calc(50% - 140px);
        z-index: 5;
        transform: rotate(-8deg) translateY(0);
      }

      &:nth-child(2) {
        left: calc(50% - 85px);
        z-index: 4;
        transform: rotate(-4deg) translateY(0);
      }

      &:nth-child(3) {
        left: calc(50% - 35px);
        z-index: 3;
        transform: rotate(0deg) translateY(0);
      }

      &:nth-child(4) {
        left: calc(50% + 15px);
        z-index: 2;
        transform: rotate(4deg) translateY(0);
      }

      &:nth-child(5) {
        left: calc(50% + 65px);
        z-index: 1;
        transform: rotate(8deg) translateY(0);
        opacity: 0.8;
      }
    `}

    &:hover {
      z-index: 1000 !important;
      transform: scale(1.3) rotate(0deg) translateY(-3px) !important;
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.8);
      border-color: rgba(102, 126, 234, 0.9);
    }
  }

  .no-thumbnails {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    border: 1px dashed rgba(102, 126, 234, 0.3);
    color: rgba(255, 255, 255, 0.4);
    font-size: 0.75rem;
  }
`

const StatsGrid = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  display: ${(props) => (props.$viewMode === 'list' ? 'flex' : 'grid')};
  ${(props) =>
    props.$viewMode === 'list'
      ? `
    gap: 1.5rem;
    flex: 1;
  `
      : `
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-top: 1rem;
  `}
`

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.2);
  backdrop-filter: blur(5px);

  .stat-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(102, 126, 234, 1);
    flex-shrink: 0;
    border: 1px solid rgba(102, 126, 234, 0.2);
  }

  .stat-content {
    flex: 1;
    min-width: 0;
  }

  .stat-label {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.25rem;
    font-weight: 500;
  }

  .stat-value {
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
  }
`

const TypeBadge = styled(Badge)<{ $type: 'training' | 'validation' }>`
  background: ${(props) =>
    props.$type === 'training'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'};
  color: white;
  font-weight: 600;
  padding: 0.4rem 0.9rem;
  border-radius: 8px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: ${(props) =>
    props.$type === 'training'
      ? '0 2px 8px rgba(102, 126, 234, 0.4)'
      : '0 2px 8px rgba(16, 185, 129, 0.4)'};
  border: 1px solid
    ${(props) =>
      props.$type === 'training' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(16, 185, 129, 0.3)'};
`

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #b0b0b0;

  .empty-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 1.5rem;
    opacity: 0.3;
  }

  .empty-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: #ffffff;
  }

  .empty-subtitle {
    font-size: 1.1rem;
  }
`

const ResultsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  color: #b0b0b0;
  font-size: 0.95rem;

  .results-count {
    font-weight: 600;
    color: #ffffff;
  }
`

const ActionBar = styled.div`
  background: transparent;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  margin-top: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
`

interface ThumbnailImageProps {
  contentId: string
  projectId?: string
  datasetId?: string
}

const ThumbnailImage: React.FC<ThumbnailImageProps> = ({ contentId, projectId, datasetId }) => {
  const { data: imageUrl } = useGetContentImageQuery(
    {
      ContentID: contentId,
      project_id: projectId || '',
      dataset_id: datasetId,
    },
    { skip: !projectId || !contentId }
  )

  if (!imageUrl) {
    return null
  }

  return (
    <div className="thumbnail">
      <img src={imageUrl} alt="Dataset thumbnail" loading="lazy" />
    </div>
  )
}

interface DatasetCardProps {
  name: string
  datasetSource: string
  isSelected: boolean
  onClick: () => void
  stats?: DatasetStats
  projectId?: string
  datasetId?: string
  viewMode?: 'grid' | 'list'
}

const DatasetCardComponent: React.FC<DatasetCardProps> = ({
  name,
  datasetSource,
  isSelected,
  onClick,
  stats,
  projectId,
  datasetId,
  viewMode = 'grid',
}) => {
  const isTraining = datasetSource.endsWith(':t')
  const totalImages = (stats?.total_unannotated_images || 0) + (stats?.total_annotated_images || 0)
  const classCount = Object.keys(stats?.annotations_per_class || {}).length
  const annotationCount = stats?.total_annotations || 0
  const completeness =
    totalImages > 0 ? Math.round(((stats?.total_annotated_images || 0) / totalImages) * 100) : 0

  // Fetch sample images for thumbnails
  const { data: sampleData } = useSampleContentQuery(
    {
      project_id: projectId || '',
      count: 5,
      source: [datasetSource],
    },
    { skip: !projectId || totalImages === 0 }
  )

  const sampleImages = sampleData?.content || []

  return (
    <DatasetCard $isSelected={isSelected} $viewMode={viewMode} onClick={onClick}>
      {isSelected && (
        <div className="selected-badge">
          <Check size={16} />
        </div>
      )}

      <Card.Body>
        {viewMode === 'list' && totalImages > 0 && (
          <ThumbnailStack $viewMode={viewMode}>
            {sampleImages.length > 0 ? (
              <div className="thumbnail-wrapper">
                {sampleImages.slice(0, 5).map((content) => (
                  <ThumbnailImage
                    key={content.id}
                    contentId={content.id}
                    projectId={projectId}
                    datasetId={datasetId}
                  />
                ))}
              </div>
            ) : (
              <div className="no-thumbnails">
                <Image size={20} />
              </div>
            )}
          </ThumbnailStack>
        )}

        <CardHeader $viewMode={viewMode}>
          <CardTitle $viewMode={viewMode}>
            <div className="dataset-name">{name}</div>
            <div className="dataset-source">{datasetSource}</div>
          </CardTitle>
          <TypeBadge $type={isTraining ? 'training' : 'validation'}>
            {isTraining ? 'Training' : 'Validation'}
          </TypeBadge>
        </CardHeader>

        {viewMode === 'grid' && totalImages > 0 && (
          <ThumbnailStack $viewMode={viewMode}>
            {sampleImages.length > 0 ? (
              <div className="thumbnail-wrapper">
                {sampleImages.slice(0, 5).map((content) => (
                  <ThumbnailImage
                    key={content.id}
                    contentId={content.id}
                    projectId={projectId}
                    datasetId={datasetId}
                  />
                ))}
              </div>
            ) : (
              <div className="no-thumbnails">
                <Image size={20} />
              </div>
            )}
          </ThumbnailStack>
        )}

        <StatsGrid $viewMode={viewMode}>
          <StatItem>
            <div className="stat-icon">
              <Tag size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Classes</div>
              <div className="stat-value">{classCount}</div>
            </div>
          </StatItem>

          <StatItem>
            <div className="stat-icon">
              <Image size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Images</div>
              <div className="stat-value">{totalImages.toLocaleString()}</div>
            </div>
          </StatItem>

          <StatItem>
            <div className="stat-icon">
              <Layers size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Annotations</div>
              <div className="stat-value">{annotationCount.toLocaleString()}</div>
            </div>
          </StatItem>

          <StatItem>
            <div className="stat-icon">
              <BarChart3 size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Complete</div>
              <div className="stat-value">{completeness}%</div>
            </div>
          </StatItem>
        </StatsGrid>
      </Card.Body>
    </DatasetCard>
  )
}

const AllDatasetsView: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)

  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })

  const { data: metadata } = useGetContentMetadataQuery(
    {
      project_id: projectId || '',
    },
    { skip: !projectId }
  )

  // Get statistics for all available datasets
  const availableDatasets = useMemo(() => metadata?.metadata?.datasource || [], [metadata])
  const datasetStats = useDatasetStats(projectId || '', project?.datasetid || '', availableDatasets)

  // Get annotation stats to access datasource_distributions for actual image counts
  const { data: annoStats } = useAnnotationStats(
    projectId || '',
    project?.datasetid || '',
    availableDatasets,
    { skip: !projectId || !project?.datasetid }
  )

  // Calculate aggregated dataset statistics
  const datasetStatistics = useMemo(() => {
    const total = availableDatasets.length
    const trainingDatasets = availableDatasets.filter((d) => d.endsWith(':t')).length
    const validationDatasets = total - trainingDatasets

    // Use actual image counts from datasource_distributions instead of estimated values
    // Filter out __aggregate__ entry if it exists
    const totalImages = annoStats?.statistics?.datasource_distributions
      ? Object.entries(annoStats.statistics.datasource_distributions)
          .filter(([key]) => key !== '__aggregate__')
          .reduce((sum, [, dist]) => sum + (dist.images || 0), 0)
      : Object.entries(datasetStats)
          .filter(([key]) => key !== '__aggregate__')
          .reduce(
            (sum, [, stats]) =>
              sum + (stats?.total_unannotated_images || 0) + (stats?.total_annotated_images || 0),
            0
          )

    const totalAnnotations = annoStats?.statistics?.datasource_distributions
      ? Object.entries(annoStats.statistics.datasource_distributions)
          .filter(([key]) => key !== '__aggregate__')
          .reduce((sum, [, dist]) => sum + (dist.annotations || 0), 0)
      : Object.entries(datasetStats)
          .filter(([key]) => key !== '__aggregate__')
          .reduce((sum, [, stats]) => sum + (stats?.total_annotations || 0), 0)

    return {
      total,
      training: trainingDatasets,
      validation: validationDatasets,
      totalImages,
      totalAnnotations,
    }
  }, [availableDatasets, datasetStats, annoStats])

  // State
  const [activeTab, setActiveTab] = useState('datasets')
  const [searchTerm, setSearchTerm] = useState('')
  const [purposeFilter, setPurposeFilter] = useState('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState('name')
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([])
  const [showUploadWizard, setShowUploadWizard] = useState(false)
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null)

  // Memoize the filtered and sorted datasets
  const filteredDatasets = useMemo(() => {
    const filtered = availableDatasets.filter((dataset) => {
      const matchesSearch = dataset.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesPurpose =
        purposeFilter === 'all' ||
        (purposeFilter === 'training' && dataset.endsWith(':t')) ||
        (purposeFilter === 'validation' && !dataset.endsWith(':t'))
      return matchesSearch && matchesPurpose
    })

    // Sort datasets
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.localeCompare(b)
        case 'images':
          const aImages =
            (datasetStats[a]?.total_unannotated_images || 0) +
            (datasetStats[a]?.total_annotated_images || 0)
          const bImages =
            (datasetStats[b]?.total_unannotated_images || 0) +
            (datasetStats[b]?.total_annotated_images || 0)
          return bImages - aImages
        case 'annotations':
          const aAnnotations = datasetStats[a]?.total_annotations || 0
          const bAnnotations = datasetStats[b]?.total_annotations || 0
          return bAnnotations - aAnnotations
        case 'completeness':
          const aTotal =
            (datasetStats[a]?.total_unannotated_images || 0) +
            (datasetStats[a]?.total_annotated_images || 0)
          const bTotal =
            (datasetStats[b]?.total_unannotated_images || 0) +
            (datasetStats[b]?.total_annotated_images || 0)
          const aCompleteness =
            aTotal > 0 ? ((datasetStats[a]?.total_annotated_images || 0) / aTotal) * 100 : 0
          const bCompleteness =
            bTotal > 0 ? ((datasetStats[b]?.total_annotated_images || 0) / bTotal) * 100 : 0
          return bCompleteness - aCompleteness
        default:
          return 0
      }
    })

    return filtered
  }, [availableDatasets, searchTerm, purposeFilter, sortBy, datasetStats])

  const handleDatasetSelect = useCallback((datasetSource: string) => {
    setSelectedDatasets((prev) => {
      if (prev.includes(datasetSource)) {
        return prev.filter((d) => d !== datasetSource)
      } else {
        return [...prev, datasetSource]
      }
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    if (selectedDatasets.length === filteredDatasets.length) {
      setSelectedDatasets([])
    } else {
      setSelectedDatasets(filteredDatasets)
    }
  }, [selectedDatasets.length, filteredDatasets])

  const handleUseSelected = useCallback(() => {
    dispatch(setDatasetSources(selectedDatasets))
  }, [dispatch, selectedDatasets])

  const handleViewDataset = useCallback(
    (datasetSource: string) => {
      // Navigate to dataset detail view
      const encodedDatasetId = encodeURIComponent(datasetSource)
      navigate(`/datasets/${projectId}/detail/${encodedDatasetId}`)
    },
    [navigate, projectId]
  )

  // Tab items
  const tabItems = [
    {
      key: 'datasets',
      label: 'Datasets',
      icon: <Database size={20} />,
    },
    {
      key: 'displacement',
      label: 'Displacement Chart',
      icon: <BarChart3 size={20} />,
    },
    {
      key: 'content',
      label: 'Manage',
      icon: <FileText size={20} />,
    },
  ]

  // Render datasets tab content
  const renderDatasetsContent = () => (
    <MainContent>
      {/* Quick Stats Bar */}
      <QuickStatsBar>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4rem' }}>
          <StatGroup>
            <QuickStat $color="#800085">
              <Package className="icon" size={12} />
              <span className="value">{datasetStatistics.total}</span>
              <span className="label">Total</span>
            </QuickStat>
            <QuickStat $color="#10b981">
              <Database className="icon" size={12} />
              <span className="value">{datasetStatistics.training}</span>
              <span className="label">Training</span>
            </QuickStat>
            <QuickStat $color="#3b82f6">
              <Database className="icon" size={12} />
              <span className="value">{datasetStatistics.validation}</span>
              <span className="label">Validation</span>
            </QuickStat>
          </StatGroup>
          <StatGroup>
            <QuickStat $color="#f59e0b">
              <Image className="icon" size={12} />
              <span className="value">{datasetStatistics.totalImages.toLocaleString()}</span>
              <span className="label">Images</span>
            </QuickStat>
            <QuickStat $color="#8b5cf6">
              <Layers className="icon" size={12} />
              <span className="value">{datasetStatistics.totalAnnotations.toLocaleString()}</span>
              <span className="label">Annotations</span>
            </QuickStat>
          </StatGroup>
        </div>
        <CreateButton onClick={() => setShowUploadWizard(true)}>
          <Plus size={18} />
          New Dataset
        </CreateButton>
      </QuickStatsBar>

      {/* Toolbar */}
      <ToolbarSection>
        <ToolbarLeft>
          <SearchWrapper>
            <Search className="search-icon" size={16} />
            <SearchInput
              type="text"
              placeholder="Search datasets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchWrapper>

          <FilterChips>
            <FilterChip
              $isActive={purposeFilter === 'all'}
              onClick={() => setPurposeFilter('all')}
              type="button"
            >
              <Filter size={14} />
              All Types
            </FilterChip>
            <FilterChip
              $isActive={purposeFilter === 'training'}
              onClick={() => setPurposeFilter('training')}
              type="button"
            >
              Training
            </FilterChip>
            <FilterChip
              $isActive={purposeFilter === 'validation'}
              onClick={() => setPurposeFilter('validation')}
              type="button"
            >
              Validation
            </FilterChip>
          </FilterChips>
        </ToolbarLeft>

        <ToolbarRight>
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" size="sm">
              <ArrowUpDown size={16} className="me-2" />
              Sort by {sortBy}
            </Dropdown.Toggle>
            <Dropdown.Menu variant="dark">
              <Dropdown.Item onClick={() => setSortBy('name')}>Name</Dropdown.Item>
              <Dropdown.Item onClick={() => setSortBy('images')}>Images</Dropdown.Item>
              <Dropdown.Item onClick={() => setSortBy('annotations')}>Annotations</Dropdown.Item>
              <Dropdown.Item onClick={() => setSortBy('completeness')}>Completeness</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <ViewToggle>
            <Button
              variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid size={16} />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
            </Button>
          </ViewToggle>
        </ToolbarRight>
      </ToolbarSection>

      <ResultsInfo>
        <div>
          <span className="results-count">{filteredDatasets.length}</span> datasets found
        </div>
        <div>{selectedDatasets.length > 0 && <span>{selectedDatasets.length} selected</span>}</div>
      </ResultsInfo>

      {filteredDatasets.length === 0 ? (
        <EmptyState>
          <Database size={80} className="empty-icon" />
          <h3 className="empty-title">No datasets found</h3>
          <p className="empty-subtitle">
            {searchTerm || purposeFilter !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'No datasets are available in this project'}
          </p>
        </EmptyState>
      ) : (
        <CardsContainer $hasHover={hoveredCardId !== null}>
          <Row className="g-3">
            {filteredDatasets.map((datasetSource) => {
              const name = datasetSource.replace(/:t$/, '')
              const isHovered = hoveredCardId === datasetSource

              return (
                <Col
                  key={datasetSource}
                  xs={12}
                  md={viewMode === 'list' ? 12 : 6}
                  lg={viewMode === 'list' ? 12 : 4}
                >
                  <div
                    className={`dataset-card-wrapper ${isHovered ? 'is-hovered' : ''}`}
                    onMouseEnter={() => setHoveredCardId(datasetSource)}
                    onMouseLeave={() => setHoveredCardId(null)}
                  >
                    <DatasetCardComponent
                      name={name}
                      datasetSource={datasetSource}
                      isSelected={selectedDatasets.includes(datasetSource)}
                      onClick={() => handleViewDataset(datasetSource)}
                      stats={datasetStats[datasetSource]}
                      projectId={projectId || undefined}
                      datasetId={project?.datasetid || undefined}
                      viewMode={viewMode}
                    />
                  </div>
                </Col>
              )
            })}
          </Row>
        </CardsContainer>
      )}

      {selectedDatasets.length > 0 && (
        <ActionBar>
          <div>
            <strong>{selectedDatasets.length}</strong> datasets selected
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary" onClick={handleSelectAll}>
              {selectedDatasets.length === filteredDatasets.length ? 'Deselect All' : 'Select All'}
            </button>
            <button className="btn btn-primary" onClick={handleUseSelected}>
              <Check size={16} className="me-2" />
              Use Selected Datasets
            </button>
          </div>
        </ActionBar>
      )}
    </MainContent>
  )

  return (
    <>
      <PageWithTabs
        tabs={tabItems}
        activeTab={activeTab}
        onTabSelect={(k) => setActiveTab(k || 'datasets')}
        tabContent={
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'datasets')}>
            <Tab.Content>
              <Tab.Pane eventKey="datasets">{renderDatasetsContent()}</Tab.Pane>
              <Tab.Pane eventKey="displacement">
                <DisplacementChart isLoading={false} onStatsUpdate={() => undefined} />
              </Tab.Pane>
              <Tab.Pane eventKey="content">
                <DatasetManageContent showAllDatasets={true} />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        }
        children={null}
      />
      <UploadWizard show={showUploadWizard} onHide={() => setShowUploadWizard(false)} />
    </>
  )
}

export default AllDatasetsView
